import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Plus, Trash2, Search, ArrowLeft, FileText, Save, Wand2 } from 'lucide-react'
import type { SystemSkill, SystemSkillDatabase, SkillAnalysisResult } from '@creanizer/types'
import { useSystemSkillStore } from '../../../../stores/systemSkillStore'
import { SkillAnalyzer } from './SkillAnalyzer'

interface SkillDatabaseEditorProps {
  database: SystemSkillDatabase
  onBack: () => void
}

export function SkillDatabaseEditor({ database, onBack }: SkillDatabaseEditorProps) {
  const { addSkill, updateSkill, removeSkill, importFromText, searchSkills } = useSystemSkillStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [showBulkImport, setShowBulkImport] = useState(false)
  const [showAiAnalyze, setShowAiAnalyze] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [importResult, setImportResult] = useState<string | null>(null)

  // 新規スキルフォーム
  const [newSkillName, setNewSkillName] = useState('')
  const [newSkillDesc, setNewSkillDesc] = useState('')
  const [newSkillProps, setNewSkillProps] = useState('')

  const filteredSkills = searchQuery
    ? searchSkills(database.id, searchQuery)
    : database.skills

  const handleAddSkill = () => {
    if (!newSkillName.trim()) return
    let properties: Record<string, any> | undefined
    if (newSkillProps.trim()) {
      try {
        properties = JSON.parse(newSkillProps)
      } catch {
        properties = { raw: newSkillProps }
      }
    }
    addSkill(database.id, {
      name: newSkillName.trim(),
      description: newSkillDesc.trim() || undefined,
      properties
    })
    setNewSkillName('')
    setNewSkillDesc('')
    setNewSkillProps('')
    setShowAddForm(false)
  }

  const handleBulkImport = () => {
    if (!bulkText.trim()) return
    const count = importFromText(database.id, bulkText)
    setImportResult(`${count}件のスキルをインポートしました`)
    setBulkText('')
    setTimeout(() => setImportResult(null), 3000)
  }

  const handleAiImport = (skills: SkillAnalysisResult['skills']) => {
    skills.forEach(skill => {
      addSkill(database.id, {
        name: skill.name,
        description: skill.description,
        properties: {
          ...(skill.category ? { category: skill.category } : {}),
          ...(skill.baseValue !== undefined ? { baseValue: skill.baseValue } : {}),
          ...(skill.relatedStat ? { relatedStat: skill.relatedStat } : {}),
          ...skill.properties
        }
      })
    })
    setImportResult(`${skills.length}件のスキルをインポートしました`)
    setShowAiAnalyze(false)
    setTimeout(() => setImportResult(null), 3000)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <CardTitle>{database.systemName}</CardTitle>
          {database.version && <Badge variant="secondary">{database.version}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ツールバー */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => { setShowAddForm(true); setShowBulkImport(false); setShowAiAnalyze(false) }}>
            <Plus className="w-4 h-4 mr-1" />スキル追加
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setShowBulkImport(true); setShowAddForm(false); setShowAiAnalyze(false) }}>
            <FileText className="w-4 h-4 mr-1" />一括インポート
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setShowAiAnalyze(true); setShowAddForm(false); setShowBulkImport(false) }}>
            <Wand2 className="w-4 h-4 mr-1" />AI解析
          </Button>
        </div>

        {/* 検索 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="スキルを検索..."
            className="pl-9"
          />
        </div>

        {importResult && (
          <Alert><AlertDescription>{importResult}</AlertDescription></Alert>
        )}

        {/* 新規追加フォーム */}
        {showAddForm && (
          <div className="p-4 border rounded-lg space-y-3 bg-gray-50">
            <div>
              <Label>スキル名</Label>
              <Input
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                placeholder="例: 目星"
                className="mt-1"
              />
            </div>
            <div>
              <Label>説明（オプション）</Label>
              <Textarea
                value={newSkillDesc}
                onChange={(e) => setNewSkillDesc(e.target.value)}
                placeholder="スキルの説明"
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label>プロパティ（オプション・JSON形式または自由記述）</Label>
              <Textarea
                value={newSkillProps}
                onChange={(e) => setNewSkillProps(e.target.value)}
                placeholder='例: {"baseValue": 25, "category": "探索系"}'
                rows={2}
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddSkill}><Save className="w-4 h-4 mr-1" />追加</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>キャンセル</Button>
            </div>
          </div>
        )}

        {/* 一括インポート */}
        {showBulkImport && (
          <div className="p-4 border rounded-lg space-y-3 bg-gray-50">
            <div>
              <Label>スキルリスト（1行1スキル）</Label>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={`形式例:\n目星\n図書館: 本や文献を調べる\n聞き耳 (探索系): 物音を聞き取る`}
                rows={8}
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                形式: スキル名 / スキル名: 説明 / スキル名 (カテゴリ): 説明
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleBulkImport}>インポート</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowBulkImport(false)}>キャンセル</Button>
            </div>
          </div>
        )}

        {/* AI解析 */}
        {showAiAnalyze && (
          <div className="p-4 border rounded-lg bg-gray-50">
            <SkillAnalyzer
              systemName={database.systemName}
              onImport={handleAiImport}
              onCancel={() => setShowAiAnalyze(false)}
            />
          </div>
        )}

        {/* スキル一覧 */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredSkills.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              {searchQuery ? '検索結果がありません' : 'スキルがありません'}
            </p>
          ) : (
            filteredSkills.map(skill => (
              <SkillItem
                key={skill.id}
                skill={skill}
                databaseId={database.id}
                onRemove={() => removeSkill(database.id, skill.id)}
              />
            ))
          )}
        </div>
        <p className="text-xs text-gray-500">合計: {database.skills.length}件</p>
      </CardContent>
    </Card>
  )
}

function SkillItem({ skill, databaseId, onRemove }: { skill: SystemSkill; databaseId: string; onRemove: () => void }) {
  return (
    <div className="p-3 border rounded-lg hover:bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{skill.name}</span>
            {skill.properties?.category && (
              <Badge variant="outline" className="text-xs">{skill.properties.category}</Badge>
            )}
            {skill.properties?.baseValue !== undefined && (
              <Badge variant="secondary" className="text-xs">基本値: {skill.properties.baseValue}</Badge>
            )}
          </div>
          {skill.description && (
            <p className="text-sm text-gray-600 mt-1">{skill.description}</p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  )
}
